const BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  "http://127.0.0.1:8000/api/v1";

const parseJson = async (response: Response) => {
  const body = await response.json().catch(() => null);

  if (!response.ok) {
    const message =
      body?.message ||
      response.statusText ||
      "API request failed";
    throw new Error(message);
  }

  return body;
};

export const loginApi = async (
  email: string,
  password: string
) => {
  const response = await fetch(
    `${BASE_URL}/login`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email,
        password,
      }),
    }
  );

  const body = await parseJson(response);

  if (!body?.success) {
    throw new Error(
      body?.message ||
        "Login failed"
    );
  }

  return body.data;
};

export const verifyOtpApi = async (
  email: string,
  otp: string
) => {
  const response = await fetch(
    `${BASE_URL}/otp/verify`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email,
        otp,
      }),
    }
  );

  const body = await parseJson(response);

  if (!body?.success) {
    throw new Error(
      body?.message ||
        "OTP verification failed"
    );
  }

  return body.data;
};

export const forgotPasswordApi = async (email: string) => {
  const response = await fetch(`${BASE_URL}/forgot-password`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ email }),
  });

  const body = await parseJson(response);

  if (!body?.success) {
    throw new Error(body?.message || "Forgot password request failed");
  }

  return body.data;
};

export const validateForgotPasswordTokenApi = async (token: string) => {
  const response = await fetch(`${BASE_URL}/forgot-password/${token}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  const body = await parseJson(response);

  if (!body?.success) {
    throw new Error(body?.message || "Reset link is invalid or expired");
  }

  return body.data;
};

export const resetForgotPasswordApi = async (
  email: string,
  loginId: number,
  token: string,
  newPassword: string
) => {
  const response = await fetch(`${BASE_URL}/forgot-password/reset`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      email,
      login_id: loginId,
      token,
      new_password: newPassword,
    }),
  });

  const body = await parseJson(response);

  if (!body?.success) {
    throw new Error(body?.message || "Password reset failed");
  }

  return body.data;
};

export const getActivitiesApi = async () => {
  return [
    "dashboard",
    "send-money",
    "history",
    "register-retailer",
    "add-bank",
    "bank-charges",
    "approve-payment",
    "settings",
    "sms-settings",
  ];
};

export const getBanksApi = async (token?: string) => {
  const response = await fetch(`${BASE_URL}/banks/banks`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  return await parseJson(response);
};

export const getActiveBanksApi = async (token?: string) => {
  const response = await fetch(`${BASE_URL}/banks/active-banks`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  return await parseJson(response);
};

export const createBankApi = async (payload: any, token?: string) => {
  const response = await fetch(`${BASE_URL}/banks/banks`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload),
  });

  return await parseJson(response);
};

export const updateBankApi = async (
  bankId: number | string,
  payload: any,
  token?: string
) => {
  const response = await fetch(`${BASE_URL}/banks/banks/${bankId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload),
  });

  return await parseJson(response);
};

export const getSmsConfigsApi = async (token?: string) => {
  const response = await fetch(`${BASE_URL}/sms-config`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  return await parseJson(response);
};

export const createSmsConfigApi = async (payload: any, token?: string) => {
  const response = await fetch(`${BASE_URL}/sms-config`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload),
  });

  return await parseJson(response);
};

export const updateSmsConfigApi = async (
  configId: number | string,
  payload: any,
  token?: string
) => {
  const response = await fetch(`${BASE_URL}/sms-config/${configId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload),
  });

  return await parseJson(response);
};

export const getApplicationStatusApi = async (token?: string) => {
  const response = await fetch(`${BASE_URL}/application-status`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  const body = await parseJson(response);
  const payload = body?.data ?? body;
  const status =
    payload?.APP_RUNNING_STATUS ??
    payload?.app_running_status ??
    payload?.status;

  return {
    success: true,
    status: typeof status === "string" ? status.toLowerCase() : status,
    raw: body,
  };
};

export const updateApplicationStatusApi = async (
  status: "on" | "off",
  token?: string
) => {
  const response = await fetch(`${BASE_URL}/application-status`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ APP_RUNNING_STATUS: status }),
  });

  const body = await parseJson(response);
  return body;
};

export const getApplicationMessageApi = async (token?: string) => {
  const response = await fetch(`${BASE_URL}/application-settings/message`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  const body = await parseJson(response);
  return body;
};

export const updateApplicationMessageApi = async (
  payload: { message: string },
  token?: string
) => {
  const response = await fetch(`${BASE_URL}/application-settings/message`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload),
  });

  const body = await parseJson(response);
  return body;
};

export const searchCustomerByMobile = async (
  mobileNumber: string,
  token?: string,
  loginId?: number | string
) => {
  // For search we want to return the API body even on 4xx so caller can
  // decide how to handle 'not found' cases instead of parseJson throwing.
  const response = await fetch(`${BASE_URL}/customers/search-by-mobile`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ mobile_number: mobileNumber, login_id: loginId }),
  });

  const body = await response.json().catch(() => null);
  return body;
};

export const createBeneficiaryApi = async (
  payload: any,
  token?: string
) => {
  const response = await fetch(`${BASE_URL}/beneficiaries`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload),
  });

  const body = await parseJson(response);
  return body;
};

export const deleteBeneficiaryApi = async (
  beneficiaryId: number | string,
  token?: string
) => {
  const response = await fetch(`${BASE_URL}/beneficiaries/${beneficiaryId}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  const body = await parseJson(response);
  return body;
};

export const createCustomerApi = async (
  payload: any,
  token?: string
) => {
  const response = await fetch(`${BASE_URL}/customers`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload),
  });

  const body = await parseJson(response);
  return body;
};

export const createRetailerApi = async (
  payload: any,
  token?: string
) => {
  const response = await fetch(`${BASE_URL}/retailers`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload),
  });

  const body = await parseJson(response);
  return body;
};

export const updateRetailerAllDetailsApi = async (
  payload: any,
  token?: string
) => {
  const response = await fetch(`${BASE_URL}/retailers/update-all-details`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload),
  });

  const body = await parseJson(response);
  return body;
};

export const getRetailersApi = async (
  params: {
    page?: number;
    per_page?: number;
    search?: string;
    admin_id_fk?: number;
  } = {},
  token?: string
) => {
  const queryParams = new URLSearchParams(
    Object.entries(params).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        acc[key] = String(value);
      }
      return acc;
    }, {} as Record<string, string>)
  ).toString();

  const url = `${BASE_URL}/retailers${queryParams ? `?${queryParams}` : ""}`;
  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  const body = await parseJson(response);
  return body;
};

export const sendTransactionApi = async (
  payload: any,
  token?: string
) => {
  const response = await fetch(`${BASE_URL}/transactions/send`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload),
  });
  const body = await parseJson(response);
  return body;
};

export const verifyMpinApi = async (
  loginId: number | string,
  mpin: string,
  token?: string
) => {
  const response = await fetch(`${BASE_URL}/verify-mpin/${loginId}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ mpin }),
  });

  const body = await parseJson(response);
  return body;
};

export const getTransactionHistory = async (
  params: {
    login_id?: number | string;
    page?: number;
    per_page?: number;
    search?: string;
    status?: string;
  } = {},
  token?: string
) => {
  const response = await fetch(`${BASE_URL}/transactions/history`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(params),
  });

  const body = await parseJson(response);
  return body;
};

export const downloadTransactionsApi = async (
  params: {
    login_id?: number | string;
    from_date?: string;
    to_date?: string;
    status?: string;
    search?: string;
  } = {},
  token?: string
) => {
  const response = await fetch(`${BASE_URL}/transactions/download`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(params),
  });

  const contentType = response.headers.get("content-type") || "";
  const contentDisposition = response.headers.get("content-disposition") || "";
  const fileNameMatch = contentDisposition.match(/filename\*?=(?:UTF-8''|"?)([^";]+)/i);
  const rawFileName = fileNameMatch?.[1] ? decodeURIComponent(fileNameMatch[1]) : "transactions.xlsx";
  const fileName = rawFileName.replace(/[\\/:*?"<>|]/g, "_");

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    const message = text || response.statusText || "Failed to download transactions";
    throw new Error(message);
  }

  const blob = await response.blob().catch(async () => {
    const text = await response.text().catch(() => "");
    return new Blob([text || ""], { type: contentType || "text/plain" });
  });

  if (!blob || blob.size === 0) {
    throw new Error("The export response was empty.");
  }

  if (contentType.includes("json") || contentType.includes("text/plain")) {
    const text = await blob.text().catch(() => "");
    if (text) {
      throw new Error(text);
    }
  }

  return { blob, fileName, contentType };
};

export const getRetailerTransactionLog = async (
  params: {
    login_id?: number | string;
    search?: string;
    from_date?: string;
    to_date?: string;
    status?: string;
  } = {},
  token?: string
) => {
  const response = await fetch(`${BASE_URL}/transactions/get-retailer-transaction-log`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(params),
  });

  const body = await parseJson(response);
  return body;
};

export const getUserProfileApi = async (
  loginId: number | string,
  token?: string
) => {
  const response = await fetch(`${BASE_URL}/profile/${loginId}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  const body = await parseJson(response);
  return body;
};

export const updateUserProfileApi = async (
  loginId: number | string,
  payload: any,
  token?: string
) => {
  const response = await fetch(`${BASE_URL}/profile/${loginId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload),
  });

  const body = await parseJson(response);
  return body;
};

export const changeUserMpinApi = async (
  loginId: number | string,
  current_mpin: string,
  new_mpin: string,
  token?: string
) => {
  const response = await fetch(`${BASE_URL}/change-mpin/${loginId}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ current_mpin, new_mpin }),
  });

  const body = await parseJson(response);
  return body;
};

export const changeTransactionStatusApi = async (
  payload: any,
  token?: string
) => {
  const response = await fetch(`${BASE_URL}/transactions/change`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload),
  });

  const body = await parseJson(response);
  return body;
};

export const getTransactionSummary = async (payload: any = {}, token?: string) => {
  console.log("Fetching transaction summary with payload:", payload);
  const response = await fetch(`${BASE_URL}/transactions/summary`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload),
  });

  const body = await parseJson(response);
  return body;
};
export const addRetailerBalanceApi = async (
  login_id: number | string,
  amount: number,
  description: string,
  token?: string
) => {
  const response = await fetch(
    `${BASE_URL}/retailers/${login_id}/wallet/credit`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ amount, description }),
    }
  );
 
  return await parseJson(response);
};

export const debitRetailerWalletApi = async (
  login_id: number | string,
  amount: number,
  description: string,
  token?: string
) => {
  const response = await fetch(
    `${BASE_URL}/retailers/${login_id}/wallet/debit`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ amount, description }),
    }
  );
 
  return await parseJson(response);
};
export const getRetailerWalletHistory = async (
  params: {
    login_id?: number | string;
    transaction_type?: string;
    search?: string;
    from?: string;
    to?: string;
    per_page?: number;
    page?: number;
  } = {},
  token?: string
) => {
  const response = await fetch(`${BASE_URL}/wallet/get-history`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(params),
  });

  const body = await parseJson(response);
  return body;
};
export const getAdminBalanceLedger = async (
  params: {
    login_id?: number | string;
    transaction_type?: string;
    search?: string;
    retailer_name?: string;
    mobile_no?: string;
    from?: string;
    to?: string;
    date?: string;
    per_page?: number;
    page?: number;
  } = {},
  token?: string
) => {
  const response = await fetch(`${BASE_URL}/admin/balance-ledger`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(params),
  });

  const body = await parseJson(response);
  return body;
};

export const getRetailerAdminBalanceLedger = async (
  params: {
    login_id?: number | string;
    retailer_id?: number | string;
    per_page?: number;
    page?: number;
  } = {},
  token?: string
) => {
  const response = await fetch(`${BASE_URL}/admin/balance-ledger/retailer`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(params),
  });

  const body = await parseJson(response);
  return body;
};

export const submitRemarkApi = async (
  payload: any,
  token?: string
) => {
  const response = await fetch(`${BASE_URL}/bank-status`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload),
  });

  const body = await parseJson(response);
  return body;
};
export const getBankStatusApi = async (
  payload: any,
  token?: string
) => {
  const normalizedPayload = {
    ...payload,
    ...(payload?.login_id !== undefined && payload?.login_id !== null
      ? { login_id: String(payload.login_id) }
      : {}),
  };

  const response = await fetch(`${BASE_URL}/get-bank-status`, {
    method: "POST", // ✅ POST method
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(normalizedPayload), // ✅ login_id body me ja raha hai
  });

  const body = await parseJson(response);
  return body;
};